/* ============================================================================
   ATD - Video Pop 1 — controlador
   Web component <atd-video-pop1>.

   Estados:
   - Mini flotante: <video autoplay muted loop playsinline> — el navegador lo
     arranca solo. Clic/Enter/Espacio → abre el lightbox.
   - Lightbox: su PROPIO <video> (sin autoplay/muted/loop). Al abrir:
     currentTime = 0, muted = false y play() SINCRÓNICO dentro del handler del
     gesto (iOS/Safari/Chrome solo permiten audio autorizado por un gesto —
     mismo patrón que atd-video1 click_sound). Sin loop: al terminar muestra
     el botón ▶ de replay (decisión de Alexis; la referencia e-sorella se
     queda congelada en el último cuadro).
   - Cerrar (X, backdrop o Esc) → pausa el lightbox, reanuda el mini mudo.
   - X del mini → oculta el widget; opcionalmente lo recuerda en
     sessionStorage (nunca en el theme editor).

   Sin dependencias. Idempotente (dataset.atdReady).
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('atd-video-pop1')) {
    return;
  }

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  class ATDVideoPop1 extends HTMLElement {
    connectedCallback() {
      if (this.dataset.atdReady === 'true') return;
      this.dataset.atdReady = 'true';

      this.mini = this.querySelector('[data-atd-vp-mini]');
      this.miniVideo = this.querySelector('[data-atd-vp-mini-video]');
      this.lightbox = this.querySelector('[data-atd-vp-lightbox]');
      this.video = this.querySelector('[data-atd-vp-video]');
      this.playBtn = this.querySelector('[data-atd-vp-play]');
      this.closeBtn = this.querySelector('[data-atd-vp-close]');
      this.backdrop = this.querySelector('[data-atd-vp-backdrop]');
      this.dismissBtn = this.querySelector('[data-atd-vp-dismiss]');

      if (!this.mini || !this.lightbox || !this.video) return;

      this.storageKey = 'atd-video-pop1:' + (this.dataset.sectionId || '') + ':closed';
      this._prevOverflow = null;
      this._onKeydown = null;

      // Cierre recordado en esta sesión (nunca dentro del theme editor).
      if (!DESIGN_MODE && this.dataset.remember === 'true') {
        try {
          if (window.sessionStorage.getItem(this.storageKey) === '1') {
            this.style.display = 'none';
            return;
          }
        } catch (e) {
          /* sessionStorage bloqueado: seguir sin memoria */
        }
      }

      // --- Mini: abrir lightbox (clic + teclado, es un role="button") ---
      this.mini.addEventListener('click', () => this._open());
      this.mini.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          this._open();
        }
      });

      // --- X del mini: ocultar el widget (no abrir el lightbox) ---
      if (this.dismissBtn) {
        this.dismissBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.style.display = 'none';
          if (!DESIGN_MODE && this.dataset.remember === 'true') {
            try {
              window.sessionStorage.setItem(this.storageKey, '1');
            } catch (e) {
              /* sin memoria de sesión, solo oculta */
            }
          }
        });
      }

      // --- Lightbox: cerrar por X o backdrop ---
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this._close());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this._close());

      // --- Video del lightbox: estados ---
      // Botón central visible solo cuando NO está reproduciendo.
      this.video.addEventListener('play', () => {
        if (this.playBtn) this.playBtn.hidden = true;
      });
      this.video.addEventListener('pause', () => {
        if (this.playBtn && !this.lightbox.hidden) this.playBtn.hidden = false;
      });
      this.video.addEventListener('ended', () => {
        if (this.playBtn) this.playBtn.hidden = false;
      });

      // Reproduciendo → clic en el video pausa (el botón está oculto).
      this.video.addEventListener('click', () => {
        if (!this.video.paused) this.video.pause();
      });

      // Botón central: replay desde 0 si terminó; si estaba pausado, reanuda.
      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => {
          if (this.video.ended) this.video.currentTime = 0;
          this._playWithSound();
        });
      }
    }

    disconnectedCallback() {
      // Si el editor re-renderiza la sección con el lightbox abierto,
      // no dejar el scroll de la página bloqueado.
      this._unlockScroll();
    }

    _open() {
      this.lightbox.hidden = false;
      this._lockScroll();
      if (this.miniVideo) this.miniVideo.pause();
      if (this.playBtn) this.playBtn.hidden = true;

      // Arranca DESDE CERO, con sonido, sin loop (el markup ya no trae loop).
      this.video.currentTime = 0;
      this._playWithSound();

      this._onKeydown = (ev) => {
        if (ev.key === 'Escape') this._close();
      };
      document.addEventListener('keydown', this._onKeydown);
      if (this.closeBtn) this.closeBtn.focus({ preventScroll: true });
    }

    _close() {
      this.video.pause();
      this.lightbox.hidden = true;
      this._unlockScroll();
      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
        this._onKeydown = null;
      }
      // El mini vuelve a su loop mudo.
      if (this.miniVideo) {
        this.miniVideo.muted = true;
        var p = this.miniVideo.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
      this.mini.focus({ preventScroll: true });
    }

    _playWithSound() {
      // play() SINCRÓNICO dentro del gesto: así el navegador autoriza el audio.
      this.video.muted = false;
      var p = this.video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Si el navegador bloquea el play con sonido, reintenta mudo para
          // no dejar el lightbox roto.
          this.video.muted = true;
          this.video.play().catch(function () {});
        });
      }
    }

    _lockScroll() {
      if (this._prevOverflow === null) {
        this._prevOverflow = document.documentElement.style.overflow || '';
        document.documentElement.style.overflow = 'hidden';
      }
    }

    _unlockScroll() {
      if (this._prevOverflow !== null) {
        document.documentElement.style.overflow = this._prevOverflow;
        this._prevOverflow = null;
      }
    }
  }

  window.customElements.define('atd-video-pop1', ATDVideoPop1);
})();
